import React from 'react';
import { Modal, Button, Typography, Icon } from '@scale/scaleui';
import { useOCPStore } from '../hooks/useOCPStore';
import { toast } from 'react-toastify';

interface PublishDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: () => Promise<void>;
  hasUnsavedSignupFlow: boolean;
}

const PublishDraftModal: React.FC<PublishDraftModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  hasUnsavedSignupFlow,
}) => {
  const { validationResults, unsavedSignupFlow, publishingDraft, setPublishingDraft } = useOCPStore(
    state => ({
      validationResults: state.validationResults,
      unsavedSignupFlow: state.unsavedSignupFlow,
      publishingDraft: state.publishingDraft,
      setPublishingDraft: state.setPublishingDraft,
    }),
  );

  const handlePublish = async () => {
    setPublishingDraft(true);
    try {
      await onPublish();
    } catch (error) {
      toast(`Error publishing flow: ${error.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
    } finally {
      setPublishingDraft(false);
    }
  };

  const modalActions = validationResults.isValid
    ? [
        <div key="Publish Changes" className="w-full">
          {publishingDraft && (
            <Typography.Paragraph3 className="mb-2 text-center text-gray-500">
              This may take a minute, do not reload the page.
            </Typography.Paragraph3>
          )}
          <Button
            variant="primary"
            onClick={handlePublish}
            className="w-full py-2 px-3 text-center justify-center text-lg font-semibold"
            disabled={publishingDraft}
          >
            {publishingDraft ? 'Publishing...' : 'Publish Changes'}
          </Button>
        </div>,
      ]
    : [];

  return (
    <Modal
      open={isOpen || publishingDraft}
      onClose={onClose}
      title="Publish Draft"
      innerClassName="overflow-visible"
      actions={modalActions}
    >
      {validationResults.isValid ? (
        <>
          <div className="flex items-center gap-x-2">
            <Icon icon="check-circle" variant="success" />
            <Typography.Paragraph1 className="m-0">
              No errors detected. Ready to publish.
            </Typography.Paragraph1>
          </div>
          {hasUnsavedSignupFlow && (
            <Typography.Paragraph3 className="mt-4 max-w-sm">
              Note: You have unsaved changes for signup flow <b>{unsavedSignupFlow.name}</b>.
              Publishing will save these changes.
            </Typography.Paragraph3>
          )}
          <Typography.Paragraph3 className="mt-4 max-w-sm">
            Note: Upon publishing all contributors currently in the flow will be migrated to the new
            version of the flow by adding them to the starting node of the new version and
            re-transitioning through the new version of the flow as far as possible. This does not
            re-trigger any trigger emails.
          </Typography.Paragraph3>
        </>
      ) : (
        <div>
          {validationResults.errors.map((error, index) => (
            <div key={index} className="flex items-center gap-x-2 my-2">
              <Icon icon="exclamation-circle" variant="danger" />
              <Typography.Paragraph3 className="text-red-500 m-0 flex-grow">
                {error.message}
              </Typography.Paragraph3>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default PublishDraftModal;
